import { useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Filter, Loader2, RefreshCw, X, Search } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useStateMachine } from "@/core/arch/state_machine";
import { MoneyRepository } from "@/application/money/money_repository";
import { InvoicesListBloc } from "@/application/money/invoices_list_bloc";
import type {
  Invoice,
  InvoiceFilter,
  InvoiceStatus,
} from "@/application/money/types";
import {
  formatRelative,
  formatAbsolute,
  defaultInvoiceFilter,
} from "@/application/money/types";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import { Badge } from "@/components/ui/base/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/base/select";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { CursorPagination } from "@/components/ui/data-table/CursorPagination";
import { MoneyStatusBadge } from "./components/MoneyStatusBadge";
import { AmountCell } from "./components/AmountCell";
import { CopyButton } from "./components/CopyButton";

// ── Date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS: {
  label: string;
  value: Exclude<InvoiceFilter["dateRange"]["preset"], "custom">;
}[] = [
  { label: "Last 1 hour", value: "1h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
];

const INVOICE_STATUSES: InvoiceStatus[] = [
  "Draft",
  "Sent",
  "Paid",
  "Cancelled",
];

// ── Filter toolbar ────────────────────────────────────────────────────────────

interface FilterToolbarProps {
  filters: InvoiceFilter;
  onFilterChange: (patch: Partial<InvoiceFilter>) => void;
  onReset: () => void;
}

function FilterToolbar({
  filters,
  onFilterChange,
  onReset,
}: FilterToolbarProps) {
  const activeDateLabel =
    filters.dateRange.preset !== "all" && filters.dateRange.preset !== "custom"
      ? (DATE_PRESETS.find((p) => p.value === filters.dateRange.preset)
          ?.label ?? null)
      : null;

  const nonDateCount = filters.statuses.length > 0 ? 1 : 0;
  const isFiltered =
    nonDateCount > 0 || activeDateLabel !== null || filters.searchQuery !== "";

  function toggleStatus(status: InvoiceStatus) {
    const current = filters.statuses;
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    onFilterChange({ statuses: next });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search input */}
      <div className="relative">
        <Search className="text-subtle-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search invoice number..."
          className="w-56 pl-9"
          value={filters.searchQuery}
          onChange={(e) => onFilterChange({ searchQuery: e.target.value })}
          aria-label="Search by invoice number"
        />
      </div>

      {/* Filters popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeDateLabel && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1.5 text-[10px] font-normal"
              >
                {activeDateLabel}
              </Badge>
            )}
            {nonDateCount > 0 && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1 text-[10px] font-normal"
              >
                +{nonDateCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="flex flex-col gap-4">
            {/* Status multi-select */}
            <div className="flex flex-col gap-1.5">
              <span className="text-muted-foreground text-xs font-medium">
                Status
              </span>
              <div className="flex flex-wrap gap-1.5">
                {INVOICE_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStatus(s)}
                    className={[
                      "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                      filters.statuses.includes(s)
                        ? "bg-blue-600 text-white"
                        : "bg-muted text-muted-foreground hover:bg-accent",
                    ].join(" ")}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                Date range
              </span>
              <Select
                value={filters.dateRange.preset}
                onValueChange={(v) => {
                  const preset = v as Exclude<
                    InvoiceFilter["dateRange"]["preset"],
                    "custom"
                  >;
                  onFilterChange({ dateRange: { preset } });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {isFiltered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onReset}
          aria-label="Reset filters"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

interface InvoicesListProps {
  businessId: string;
  /**
   * Renders a short, toolbar-less list for an overview section. The full
   * list lives on its own tab, which is where filtering belongs.
   */
  previewLimit?: number;
}

export function InvoicesList({ businessId, previewLimit }: InvoicesListProps) {
  const navigate = useNavigate();

  const [state, bloc] = useStateMachine(
    () => new InvoicesListBloc(new MoneyRepository(), businessId),
  );

  useEffect(() => {
    void bloc.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = useCallback(
    (patch: Partial<InvoiceFilter>) => {
      void bloc.setFilter(patch);
    },
    [bloc],
  );

  const handleReset = useCallback(() => {
    void bloc.setFilter(defaultInvoiceFilter());
  }, [bloc]);

  const columns: ColumnDef<Invoice, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "created",
        header: "Date",
        cell: ({ row }) => {
          const created = row.getValue("created") as number | null;
          if (created == null)
            return <span className="text-subtle-foreground">—</span>;
          return (
            <span
              className="text-muted-foreground text-sm whitespace-nowrap"
              title={formatAbsolute(created)}
            >
              {formatRelative(created)}
            </span>
          );
        },
      },
      {
        accessorKey: "id",
        header: "Invoice #",
        cell: ({ row }) => {
          const id = row.getValue("id") as string;
          return (
            <span className="group flex items-center gap-1">
              <span className="text-foreground font-mono text-xs">
                {id.slice(0, 16)}…
              </span>
              <span className="opacity-0 group-hover:opacity-100">
                <CopyButton value={id} />
              </span>
            </span>
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <AmountCell
            cents={row.getValue("amount") as number | null}
            currency={row.original.currency}
          />
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.getValue("status") as string | null;
          if (!status) return <span className="text-subtle-foreground">—</span>;
          return <MoneyStatusBadge status={status} />;
        },
      },
      {
        id: "customer",
        header: "Customer",
        cell: ({ row }) => {
          const inv = row.original;
          const name =
            inv.recipient?.name ?? inv.recipient?.deliveryAddress ?? null;
          return (
            <span className="text-muted-foreground text-sm">{name ?? "—"}</span>
          );
        },
      },
      {
        id: "paid_via",
        header: "Paid via",
        cell: ({ row }) => {
          const inv = row.original;
          const linkedTx = state.paidViaMap.get(inv.id);
          if (!linkedTx?.sourceChargeId) {
            return <span className="text-subtle-foreground">—</span>;
          }
          return (
            <span className="text-muted-foreground font-mono text-xs">
              {linkedTx.sourceChargeId.slice(0, 18)}…
            </span>
          );
        },
      },
    ],

    [state.paidViaMap],
  );

  const renderToolbar = () =>
    previewLimit ? null : (
      <FilterToolbar
        filters={state.filters}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
      />
    );

  if (state.status === "loading") {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">
          Failed to load invoices: {state.error}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void bloc.load()}
          className="gap-1.5"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  const hasItems = state.items.length > 0;
  const isFiltering =
    state.filters.searchQuery !== "" ||
    state.filters.statuses.length > 0 ||
    state.filters.dateRange.preset !== "all";

  if (state.status === "loaded" && !hasItems && !isFiltering) {
    return (
      <div className="flex flex-col gap-4">
        {renderToolbar()}
        <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
          <p className="text-sm">
            This merchant hasn&apos;t created any invoices yet.
          </p>
        </div>
      </div>
    );
  }

  if (state.status === "loaded" && !hasItems && isFiltering) {
    return (
      <div className="flex flex-col gap-4">
        {renderToolbar()}
        <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
          <p className="text-sm">No invoices match these filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {state.paidViaError && (
        <div className="border-border bg-muted text-muted-foreground rounded-lg border px-4 py-2 text-sm">
          Transaction data temporarily unavailable — invoice info still shown.
        </div>
      )}
      <DataTable
        columns={columns}
        data={previewLimit ? state.items.slice(0, previewLimit) : state.items}
        onRowClick={(inv) => {
          const linkedTx = state.paidViaMap.get(inv.id);
          if (!linkedTx) return; // unpaid — no-op per spec
          navigate(
            ROUTES.TRANSACTION_DETAIL.replace(
              ":businessId",
              businessId,
            ).replace(":txId", linkedTx.id),
          );
        }}
        toolbar={renderToolbar}
        footer={
          <CursorPagination
            hasPrev={state.hasPrev}
            hasNext={state.hasNext}
            onPrev={() => void bloc.prevPage()}
            onNext={() => void bloc.nextPage()}
            // CursorPagination only renders post-load guard; buttons can't be clicked mid-fetch
            loading={false}
          />
        }
      />
    </div>
  );
}
